import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';

import Appearance from 'styles/Appearance.js';
import DatePickerDropdownField from 'views/DatePickerDropdownField.js';
import { LayerItem } from 'structure/Layer.js';
import ListField from 'views/ListField.js';
import Request from 'files/Request.js';
import TimezoneLookupField from 'views/TimezoneLookupField.js';
import User from 'classes/User.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

const BookAppointment = ({ defaultDate, onBookAppointment, onDateChange, onUserChange, onLoad, lead, request, presetUsers = {}, utils }) => {

    const [baseEntries, setBaseEntries] = useState([]);
    const [duration, setDuration] = useState(10800);
    const [entries, setEntries] = useState([]);
    const [feedbackTemplate, setFeedbackTemplate] = useState(null);
    const [feedbackTemplates, setFeedbackTemplates] = useState([]);
    const [operatingHours, setOperatingHours] = useState(null);
    const [targetDate, setTargetDate] = useState(defaultDate ? moment(defaultDate) : moment());
    const [timezone, setTimezone] = useState(lead.timezone || moment.tz.guess());
    const [user, setUser] = useState(null);
    const [users, setUsers] = useState([]);

    const isDateRangeAvailable = ({ end_date, start_date }) => {

        // all days are available if no user has been selected
        if(!user) {
            return true;
        }

        // convert dates to unix timestamps for easier comparissions
        let unixStart = moment(start_date).unix();
        let unixEnd = moment(end_date).unix();

        // check if start date and end date fall within at least one of the selected user's availability entries
        let target = entries.find(entry => entry.user.user_id === user.user_id);
        let slot = target && target.timeslots.find(timeslot => {
            return unixStart >= timeslot.start_date && unixEnd <= timeslot.end_date;
        });
        return slot && slot.available === true ? true : false;
    }

    const onBookTimeslot = async ({ start_date, end_date }) => {
        try {

            // notify subscribers that load has begun
            if(typeof(onLoad) === 'function') {
                onLoad(true);
            }
            await Utils.sleep(0.25);

            // check if client three day rule is enabled
            let preferences = utils.client.get().preferences;
            if(preferences.three_day_rule) {
                let days = moment.tz(start_date, timezone).startOf('day').diff(moment.tz(timezone).startOf('day'), 'days');
                if(days > 3) {
                    throw new Error('The client is currently accepting appointments scheduled up to three days in advance. Please speak with your client workspace admin if you have any questions.');
                }
            }

            // submit request to server
            let { id, lead_status } = await Request.post(utils, '/appointments/', {
                duration: duration,
                feedback_template_id: feedbackTemplate?.id,
                lead_id: lead && lead.id,
                primary_user_id: user && user.user_id,
                request_id: request && request.id,
                start_date: moment.tz(start_date, timezone).utc().format('YYYY-MM-DD HH:mm:ss'),
                timezone: timezone,
                type: 'new'
            });

            // notify subscribers of new fetch and load end
            utils.content.fetch('appointments');
            if(typeof(onLoad) === 'function') {
                onLoad(false);
            }

            // notify subscribers that appointment booking props are available
            if(typeof(onBookAppointment) === 'function') {
                onBookAppointment({
                    end_date: moment.tz(end_date, timezone).utc(),
                    duration: duration,
                    id: id,
                    lead_status: lead_status,
                    start_date: moment.tz(start_date, timezone).utc(),
                    timezone
                });
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue booking this appointment. ${e.message || 'An unknown error occurred'}`
            })
            if(typeof(onLoad) === 'function') {
                onLoad(false);
            }
        }
    }

    const onChangeAssignment = selectEvent => {
        let id = Utils.attributeForKey.select(selectEvent, 'id');
        if(!id) {
            setUser(null);
            return;
        }
        let entry = entries.find(entry => entry.user.user_id === parseInt(id));
        if(entry) {
            setUser(entry.user);
        }
    }

    const onTimeslotClick = (entry, available) => {
        if(available === false) {
            utils.alert.show({
                title: 'Unavailable Timeslot',
                message: `Are you sure that you want to book the ${moment(entry.start_date).format('h:mma')} to ${moment(entry.end_date).format('h:mma')} timeslot? It looks like ${user.full_name} is not available at this time`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        onBookTimeslot(entry);
                        return;
                    }
                }
            })
            return;
        }

        utils.alert.show({
            title: 'Book this Timeslot',
            message: `Are you sure that you want to book the ${moment(entry.start_date).format('h:mma')} to ${moment(entry.end_date).format('h:mma')} timeslot?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onBookTimeslot(entry);
                    return;
                }
            }
        })
    }

    const onUpdateTimezone = () => {
        console.log(timezone);
        setEntries(baseEntries.map(entry => {
            return {
                user: entry.user,
                timeslots: entry.timeslots.map(timeslot => {
                    return {
                        ...timeslot,
                        end_date: moment.utc(timeslot.end_time).tz(timezone).unix(),
                        start_date: moment.utc(timeslot.start_time).tz(timezone).unix()
                    }
                })
            }
        }));
    }

    const getAssignments = () => {
        return (
            <LayerItem
            required={false}
            title={'Closer'}>
                <select
                className={`custom-select ${window.theme}`}
                value={user ? `${user.full_name} (ID: ${user.user_id})` : 'Choose to view users...'}
                onChange={onChangeAssignment}
                style={{
                    background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                    backgroundSize: '10px',
                    width: '100%'
                }}>
                    <option>{'Click to view users...'}</option>
                    {users.map((user, index) => {
                        return (
                            <option key={index} id={user.user_id}>{`${user.full_name} (ID: ${user.user_id})`}</option>
                        )
                    })}
                </select>
            </LayerItem>
        )
    }

    const getAppointmentDateSelector = () => {
        return (
            <LayerItem
            title={'Date'}
            required={targetDate ? false : true}>
                <DatePickerDropdownField
                onChange={setTargetDate} 
                removable={false}
                utils={utils}
                value={targetDate} />
            </LayerItem>
        )
    }

    const getDurationSelect = () => {

        // duration is the total length of the timeslot itself
        let durations = [ 3600, 5400, 7200, 9000, 10800, 12600, 14400, 16200, 18000 ];
        return (
            <LayerItem
            title={'Duration'}
            required={duration ? false : true}
            childrenStyle={{
                paddingLeft: 0,
                paddingRight: 0
            }}>
                <select
                className={`custom-select ${window.theme}`}
                onChange={e => {
                    let id = Utils.attributeForKey.select(e, 'id');
                    setDuration(parseInt(id));
                }}
                style={{
                    background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                    backgroundSize: '10px',
                    width: '100%'
                }}
                value={Utils.parseDuration(duration)}>
                    {durations.map((duration, index) => {
                        return (
                            <option key={index} id={duration}>{Utils.parseDuration(duration)}</option>
                        )
                    })}
                </select>
            </LayerItem>
        )
    }

    const getFeedbackTemplateSelector = () => {
        return null;
        return (
            <LayerItem
            required={false}
            title={'Customer Feedback Template'}>
                <ListField
                items={feedbackTemplates}
                value={feedbackTemplate ? feedbackTemplate.title : null}
                onChange={template => setFeedbackTemplate(template)} />
            </LayerItem>
        )
    }

    const getTimeslots = () => {

        // render timeslots options
        // spacing is the amount of time between each beginning timeslot value
        let spacing = 1800;
        let length = (duration / 60 / 60);

        // use client hours of operation if applicable
        // set startHour to 5:00am and endHour to 12:00am if no operating hours are found
        let endHour = 24;
        let startHour = 5;
        let { end, start } = operatingHours || {};
        if(end && start) {

            // extract hour value from start and end operating times
            endHour = moment(end, 'HH:mm:ss').hours();
            startHour = moment(start, 'HH:mm:ss').hours();

            // convert ending hour to 24 if midnight is represented as hour 0
            if(endHour === 0) {
                endHour = 24;
            }
        }

        // set starting date and ending date use startHour and endHour
        let startDate = moment(targetDate).tz(timezone).startOf('day').add(startHour, 'hours');
        let max = moment(targetDate).tz(timezone).startOf('day').add(endHour + length, 'hours');

        // add a day to the max date if the end hour comes before the start hour
        // this means that the ending time is for the next day
        if(endHour < startHour) {
            max = max.add(1, 'days');
        }

        // create array of start values spaced out by "spacing" value
        // create array of timeslots starting at "start" date and ending at "max" date
        let range = (max.unix() - startDate.unix()) / 1800;
        let starts = [...new Array(range >= 0 ? range : 0)].map((_, index) => startDate.clone().add(index * spacing, 'seconds'));
        let slots = starts.map(date => {
            return {
                start_date: date,
                end_date: date.clone().add(duration, 'seconds')
            }
        })

        // filter down to acceptable timeslots found within the start->max window
        let timeslots = slots.filter(dates => {
            return dates.start_date <= max && dates.end_date <= max;
        });

        // available slots for user based on override state
        return (
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'Timeslot'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    maxHeight: 500,
                    overflowY: 'scroll',
                    width: '100%'
                }}>
                    {timeslots.map((dates, index) => {
                        let available = isDateRangeAvailable(dates);
                        return (
                            <div
                            key={index}
                            className={`view-entry ${window.theme}`}
                            onClick={onTimeslotClick.bind(this, dates, available)}>
                                {Views.entry({
                                    bottomBorder: index !== timeslots.length - 1,
                                    icon: {
                                        path: `images/${available ? 'plus-button-green-small' : 'prohibitory-red-small'}.png`
                                    },
                                    subTitle: available ? `Timeslot for ${Utils.parseDuration(duration)}` : `Not Available for ${user.full_name}`,
                                    title: `${dates.start_date.format('h:mma')} to ${dates.end_date.format('h:mma')}`
                                })}
                            </div>
                        )
                    })}
                </div>
            </LayerItem>
        )
    }

    const getTimezoneSelector = () => {
        return (
            <LayerItem
            title={'Timezone'}
            required={targetDate ? false : true}>
                <TimezoneLookupField
                defaultValue={moment.tz.guess()}
                onChange={setTimezone} 
                utils={utils} 
                value={timezone} />
            </LayerItem>
        )
    }

    const fetchUsers = async () => {
        try {

            // fetch user availability for the target date
            let { users } = await Request.get(utils, '/clients/', {
                date: targetDate.format('YYYY-MM-DD'),
                type: 'availability'
            });

            // update local state with users and their availability entries
            setUsers(users.map(entry => entry.user));
            setBaseEntries(users.map(entry => {
                return {
                    timeslots: entry.timeslots,
                    user: User.create(entry.user)
                }
            }));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const setupTarget = async () => {
        try {
            let { operating_hours, templates } = await Request.get(utils, '/leads/', {
                type: 'booking_targets',
                paging: false
            });

            // set operating hours for client and default feedback template
            setOperatingHours(operating_hours?.appointments ?? null);
            //setFeedbackTemplates(templates.map(template => Feedback.Template.create(template)));

            // set default appointment duration
            let preferences = utils.client.get().preferences;
            setDuration(preferences.default_appointment_duration?.value ? (preferences.default_appointment_duration.value * 60 * 60) : 10800);

            // attach users if applicable
            let { assigned } = presetUsers;
            if(assigned) {
                setUser(assigned);
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up the information for this window. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        onUpdateTimezone();
    }, [baseEntries, timezone]);

    useEffect(() => {
        if(typeof(onDateChange) === 'function') {
            onDateChange(targetDate);
        }
    }, [targetDate])

    useEffect(() => {
        if(typeof(onUserChange) === 'function') {
            onUserChange(user);
        }
    }, [user])

    useEffect(() => {
        fetchUsers();
    }, [targetDate]);

    useEffect(() => {
        setupTarget();
        utils.events.on('book_appointment', 'client_preferences_update', setupTarget);
        return () => {
            utils.events.off('book_appointment', 'client_preferences_update', setupTarget);
        }
    }, []);

    return (
        <>
        {getAppointmentDateSelector()}
        {getTimezoneSelector()}
        {getDurationSelect()}
        {getAssignments()}
        {getFeedbackTemplateSelector()}
        {getTimeslots()}
        </>
    )
}

export default BookAppointment;
